// Client-only "Save my car location" storage helpers.
// No backend, no network, no account. Data lives in localStorage only.

const KEY = "spotdrop_car_location";

/**
 * Shape stored under KEY:
 * { lat: number, lng: number, accuracy_m: number, saved_at: number (ms epoch), note: string }
 */

export function getCarLocation() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.lat !== "number" ||
      typeof parsed?.lng !== "number" ||
      typeof parsed?.saved_at !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveCarLocation({ lat, lng, accuracy_m, note }) {
  const payload = {
    lat: Number(lat),
    lng: Number(lng),
    accuracy_m: Number.isFinite(accuracy_m) ? Math.round(accuracy_m) : null,
    saved_at: Date.now(),
    note: (note || "").trim().slice(0, 60),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(payload));
    return payload;
  } catch {
    throw new Error("storage_blocked");
  }
}

export function clearCarLocation() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

// Formats a saved_at timestamp as "just now", "5 minutes ago", "3 hours ago",
// "yesterday", or a "MMM d" date fallback. Kept small and dependency-free.
export function formatRelativeTime(msEpoch, now = Date.now()) {
  if (!Number.isFinite(msEpoch)) return "";
  const diffSec = Math.round((now - msEpoch) / 1000);
  if (diffSec < 45) return "just now";
  if (diffSec < 90) return "a minute ago";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 45) return `${diffMin} minutes ago`;
  if (diffMin < 90) return "an hour ago";
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 22) return `${diffHr} hours ago`;
  if (diffHr < 36) return "yesterday";
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} days ago`;
  try {
    return new Date(msEpoch).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return `${diffDay} days ago`;
  }
}

// Stale iff saved more than 24 hours ago.
export function isStaleSave(msEpoch, now = Date.now()) {
  if (!Number.isFinite(msEpoch)) return false;
  return now - msEpoch > 24 * 60 * 60 * 1000;
}

// Haversine distance in metres (client-side, no dep).
export function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// User-friendly distance string. Returns null when input can't be trusted.
export function formatDistance(meters) {
  if (!Number.isFinite(meters) || meters < 0) return null;
  if (meters < 100) return "nearby";
  if (meters < 1000) return `~ ${Math.round(meters / 10) * 10} m away`;
  return `~ ${(meters / 1000).toFixed(1)} km away`;
}

// Builds an external navigation URL (Apple Maps on iOS, Google Maps elsewhere).
export function buildNavigationUrl(lat, lng, userAgent = navigator.userAgent || "") {
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  if (isIOS) return `maps://?daddr=${lat},${lng}&dirflg=w`;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`;
}
